import siteSocialDefaults from '../../seo/site-social-links.json';
import { normalizeSocialField } from '@shopgnd/shared';
import type { SiteSocialLink, SiteSocialPlatform } from '@/lib/siteSocialLinks';

export type SiteSocialLinksPayload = {
  links: SiteSocialLink[];
  googleBusinessUrl?: string;
};

type StoredLink = {
  platform: string;
  href?: string;
  label?: string;
};

const FOOTER_PLATFORMS: SiteSocialPlatform[] = ['instagram', 'facebook', 'x', 'linkedin', 'youtube'];

function isFooterPlatform(value: string): value is SiteSocialPlatform {
  return (FOOTER_PLATFORMS as string[]).includes(value);
}

function defaultPayload(): SiteSocialLinksPayload {
  const links = (siteSocialDefaults.links ?? [])
    .filter((entry) => entry?.href?.trim() && isFooterPlatform(entry.platform))
    .map((entry) => ({
      platform: entry.platform as SiteSocialPlatform,
      href: entry.href.trim(),
      label: entry.label?.trim() || entry.platform,
    }));
  const googleBusinessUrl = (siteSocialDefaults as { googleBusinessUrl?: string }).googleBusinessUrl?.trim();
  return {
    links,
    googleBusinessUrl: googleBusinessUrl || undefined,
  };
}

let runtimePayload: SiteSocialLinksPayload | null = null;

export function applySiteSocialLinks(payload: SiteSocialLinksPayload): void {
  runtimePayload = payload;
}

export function getRuntimeSiteSocialLinks(): SiteSocialLinksPayload {
  return runtimePayload ?? defaultPayload();
}

function normalizeHref(platform: SiteSocialPlatform, raw: string): string | undefined {
  return normalizeSocialField(platform, raw);
}

export function parseSiteSocialLinksJson(raw: string | undefined | null): SiteSocialLinksPayload {
  if (!raw?.trim()) return defaultPayload();
  try {
    const parsed = JSON.parse(raw) as { links?: StoredLink[]; googleBusinessUrl?: string };
    const links: SiteSocialLink[] = [];
    for (const entry of parsed.links ?? []) {
      if (!entry?.platform || !isFooterPlatform(entry.platform)) continue;
      const href = normalizeHref(entry.platform, entry.href ?? '');
      if (!href) continue;
      links.push({
        platform: entry.platform,
        href,
        label: entry.label?.trim() || entry.platform,
      });
    }
    const googleBusinessUrl = parsed.googleBusinessUrl?.trim()
      ? normalizeSocialField('website', parsed.googleBusinessUrl)
      : undefined;
    if (links.length === 0 && !googleBusinessUrl) return defaultPayload();
    return { links: links.length ? links : defaultPayload().links, googleBusinessUrl };
  } catch {
    return defaultPayload();
  }
}

export function serializeSiteSocialLinks(payload: SiteSocialLinksPayload): string {
  return JSON.stringify({
    links: payload.links.map((link) => ({
      platform: link.platform,
      href: link.href,
      label: link.label,
    })),
    googleBusinessUrl: payload.googleBusinessUrl?.trim() || undefined,
  });
}

/** Official social profile URLs for schema.org sameAs (not Maps/GBP — those are not valid sameAs targets). */
export function runtimeSiteSocialSameAs(): string[] {
  return getRuntimeSiteSocialLinks().links.map((l) => l.href);
}
